import applyTimezone from 'helpers/applyTimezone'
import React, { Component } from 'react'
import { injectIntl } from 'react-intl'
import FormControl from '@material-ui/core/FormControl'
import { DateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers'
import DateFnsUtils from '@date-io/date-fns'
import moment from 'moment'
import isEqual from 'lodash/isEqual'
import {
  addDays,
  subDays,
  differenceInMinutes,
  addMinutes,
  subMinutes,
  differenceInCalendarDays,
} from 'date-fns'
import ClearIcon from '@material-ui/icons/Clear'
import { connect } from 'react-redux'
import ProfileHelper from 'helpers/profile'
import { ReactComponent as EventIcon } from 'assets/img/ic_calendar.svg'
import {
  dateToLocalISO8601DateTimeString,
  getTimeFormatMaterial,
} from 'helpers/dates'
import {
  EARLIER,
  EARLIER_OR_SAME,
  EQUAL,
  EQUAL_OR_HIGHER,
  EQUAL_OR_LOWER,
  HIGHER,
  LATER,
  LATER_OR_SAME,
  LOWER,
  SAME,
} from 'constants/index'
import { TYPE_INPUT_DATE } from 'helpers/jobs'
import messages from '../translate'

export class JobDateTimeInput extends Component {
  constructor(props) {
    super(props)

    this.state = {
      value: null,
      oldValue: null,
      evtTaskInputId: '',
      diff: differenceInMinutes,
      add: addMinutes,
      sub: subMinutes,
      isUpdating: false,
    }
  }

  onInputFocus = e => {
    if (!e.target.value) {
      const {
        dependency: { children } = {},
        onJobTaskValueRange,
        site,
        licensee,
      } = this.props
      const initialValue = applyTimezone(
        moment(),
        site?.timezone,
        licensee?.timezone,
      ).format('YYYY-MM-DD HH:mm:ss')

      onJobTaskValueRange(this.checkInvalidRange(initialValue))
      this.checkValidChildren(initialValue, children)
      this.setState(
        {
          value: initialValue,
        },
        () => {
          this.handleInputChange(false)
        },
      )
    }
  }

  componentDidMount() {
    const {
      inputData: { value = null, evtInputId },
      dependency: { dependency: { type: dependencyType } = {} } = {},
    } = this.props

    if (dependencyType === TYPE_INPUT_DATE) {
      this.setState({
        value,
        oldValue: value,
        evtTaskInputId: evtInputId,
        diff: differenceInCalendarDays,
        add: addDays,
        sub: subDays,
      })
    } else {
      this.setState({
        value,
        oldValue: value,
        evtTaskInputId: evtInputId,
        diff: differenceInMinutes,
        add: addMinutes,
        sub: subMinutes,
      })
    }
  }

  shouldComponentUpdate(nextProps) {
    const { inputInfo, inputData } = this.props
    const { inputInfo: nextInputInfo, inputData: nextInputData } = nextProps

    if (
      !isEqual(inputInfo, nextInputInfo) ||
      !isEqual(inputData, nextInputData)
    ) {
      const { value = null, evtInputId } = nextInputData

      this.setState({
        value,
        oldValue: value,
        evtTaskInputId: evtInputId,
      })

      return false
    }

    return true
  }

  componentWillUnmount() {
    const { isUnitBlockOpen } = this.props

    if (isUnitBlockOpen) {
      this.handleInputChange()
    }
  }

  setResultId = ({ id }) => {
    this.setState({ evtTaskInputId: id })
  }

  onDateChange = (value, invalidDate) => {
    const { dependency: { children } = {}, onJobTaskValueRange } = this.props

    const validatedValue = value
      ? dateToLocalISO8601DateTimeString(value)
      : value
    onJobTaskValueRange(this.checkInvalidRange(validatedValue))
    this.checkValidChildren(validatedValue, children)
    this.setState(
      {
        value: validatedValue,
      },
      () => {
        this.handleInputChange(invalidDate)
      },
    )
  }

  checkInvalidRange = value => {
    let minDate
    let maxDate
    let invalidDate = false
    const {
      inputInfo: {
        parameters: { dependency: { rule } = {} },
      },
      dependency: { dependency: { value: dependencyValue } = {} } = {},
    } = this.props
    const { diff, sub, add } = this.state
    const parsedDate = this.dateStringToLocalDate(dependencyValue)
    const parsedValue = this.dateStringToLocalDate(value)

    switch (rule) {
      case LATER:
      case HIGHER:
        minDate = add(parsedDate, 1)

        if (diff(minDate, parsedValue) > 0) {
          invalidDate = true
          minDate = parsedValue
        }

        break
      case LATER_OR_SAME:
      case EQUAL_OR_HIGHER:
        minDate = parsedDate

        if (diff(minDate, parsedValue) > 0) {
          invalidDate = true
          minDate = parsedValue
        }

        break
      case SAME:
      case EQUAL:
        minDate = parsedDate
        maxDate = parsedDate

        if (diff(minDate, parsedValue) > 0) {
          invalidDate = true
          minDate = parsedValue
        }

        if (diff(parsedValue, maxDate) > 0) {
          invalidDate = true
          maxDate = parsedValue
        }

        break
      case EARLIER_OR_SAME:
      case EQUAL_OR_LOWER:
        maxDate = parsedDate

        if (diff(parsedValue, maxDate) > 0) {
          invalidDate = true
          maxDate = parsedValue
        }

        break
      case EARLIER:
      case LOWER:
        maxDate = sub(parsedDate, 1)

        if (diff(parsedValue, maxDate) > 0) {
          invalidDate = true
          maxDate = parsedValue
        }

        break
      default:
    }

    return invalidDate
  }

  handleInputChange = () => {
    const {
      inputInfo,
      inputData: { evtInputId },
      onJobTaskAutoSave,
    } = this.props
    const { value, oldValue, evtTaskInputId } = this.state

    if (value !== oldValue) {
      this.setState({
        isUpdating: true,
      })

      onJobTaskAutoSave(
        {
          evtInputId: evtInputId || evtTaskInputId,
          result: value,
          input: inputInfo.id,
          setResultId: this.setResultId,
        },
        () => {
          this.setState({
            isUpdating: false,
          })
        },
      )

      this.setState({ oldValue: value })
    }
  }

  checkValidChildren = (value, children) => {
    const {
      dependency: {
        currentTaskID,
        currentEvtTask: { id: currentEvtTaskID },
      } = {},
      resetInvalidDates,
      clearCurrentTask,
    } = this.props
    const mainInputValue = this.dateStringToLocalDate(value)
    const invalidDates = []

    children.forEach(child => {
      const {
        id,
        result,
        parameters: { dependency: { rule } } = {},
        children,
      } = child
      let dependentValue = this.dateStringToLocalDate(result)
      let invalid = false

      switch (rule) {
        case LATER:
        case HIGHER:
          dependentValue = subDays(dependentValue, 1)

          if (differenceInMinutes(mainInputValue, dependentValue) > 0) {
            invalidDates.push(id)
            invalid = true
          }
          break
        case LATER_OR_SAME:
        case EQUAL_OR_HIGHER:
          if (differenceInMinutes(mainInputValue, dependentValue) > 0) {
            invalidDates.push(id)
            invalid = true
          }
          break
        case SAME:
        case EQUAL:
          if (
            differenceInMinutes(dependentValue, mainInputValue) > 0 ||
            differenceInMinutes(mainInputValue, dependentValue) > 0
          ) {
            invalidDates.push(id)
            invalid = true
          }
          break
        case EARLIER_OR_SAME:
        case EQUAL_OR_LOWER:
          if (differenceInMinutes(dependentValue, mainInputValue) > 0) {
            invalidDates.push(id)
            invalid = true
          }
          break
        default:
          dependentValue = addDays(dependentValue, 1)

          if (differenceInMinutes(dependentValue, mainInputValue) > 0) {
            invalidDates.push(id)
            invalid = true
          }
      }

      if (!mainInputValue) {
        invalidDates.push(id)
      }

      if (invalid) {
        this.checkValidChildren('', children)
      }
    })

    if (invalidDates.length) {
      resetInvalidDates({ invalidDates, currentTaskID, currentEvtTaskID })
      clearCurrentTask(invalidDates)
    }
  }

  dateStringToLocalDate = s => {
    let dateString = s

    if (dateString && dateString.length === 10) {
      dateString += ' 00:00:00'
    }
    if (!dateString) return null

    return new DateFnsUtils().parse(dateString, 'yyyy-MM-dd HH:mm:ss')
  }

  render() {
    const {
      disabled,
      className,
      inputInfo: {
        title,
        required,
        parameters: {
          dependency: { input, rule } = {},
          can_set_current_datetime,
        },
      },
      intl: { formatMessage },
      dependency: {
        dependency: { value: dependencyValue, title: dependencyTitle } = {},
      } = {},
      licensee: { usaDateFormat, usaTimeFormat },
    } = this.props
    const { value, diff, sub, add, isUpdating } = this.state
    const locale = ProfileHelper.getCurrentLocale()
    const drawAsterisk = required ? '*' : ''
    const dependencyEmpty = input && dependencyValue === ''
    let message = ''
    let minDate
    let maxDate
    const parsedDate = this.dateStringToLocalDate(dependencyValue)
    const parsedValue = this.dateStringToLocalDate(value)
    let invalidDate = false

    switch (rule) {
      case LATER:
      case HIGHER:
        message = `${formatMessage(messages.shouldBeLater)} ${dependencyTitle}`
        minDate = add(parsedDate, 1)

        if (diff(minDate, parsedValue) > 0) {
          invalidDate = true
          minDate = parsedValue
        }

        break
      case LATER_OR_SAME:
      case EQUAL_OR_HIGHER:
        message = `${formatMessage(
          messages.shouldBeEqOrHigher,
        )} ${dependencyTitle}`
        minDate = parsedDate

        if (diff(minDate, parsedValue) > 0) {
          invalidDate = true
          minDate = parsedValue
        }
        break
      case SAME:
      case EQUAL:
        message = `${formatMessage(messages.shouldBeEq)} ${dependencyTitle}`
        minDate = parsedDate
        maxDate = parsedDate

        if (diff(minDate, parsedValue) > 0) {
          invalidDate = true
          minDate = parsedValue
        }

        if (diff(parsedValue, maxDate) > 0) {
          invalidDate = true
          maxDate = parsedValue
        }
        break
      case EARLIER_OR_SAME:
      case EQUAL_OR_LOWER:
        message = `${formatMessage(
          messages.shouldBeEqOrLower,
        )} ${dependencyTitle}`
        maxDate = parsedDate

        if (diff(parsedValue, maxDate) > 0) {
          invalidDate = true
          maxDate = parsedValue
        }
        break
      case EARLIER:
      case LOWER:
        message = `${formatMessage(messages.shouldBeLower)} ${dependencyTitle}`
        maxDate = sub(parsedDate, 1)

        if (diff(parsedValue, maxDate) > 0) {
          invalidDate = true
          maxDate = parsedValue
        }
        break
      default:
    }

    const showHint = input && !invalidDate
    const isInputDisabled = disabled || isUpdating || dependencyEmpty
    const timeFormat = getTimeFormatMaterial(usaTimeFormat)
    const dateTimeFormat = `${
      usaDateFormat ? 'MM-dd-yyyy' : 'dd-MM-yyyy'
    } ${timeFormat}`

    return (
      <FormControl fullWidth className={className}>
        <span className="job-field-label">
          {title} {drawAsterisk}
        </span>
        <div className="job-field-wrapper">
          <EventIcon className="job-field-icon" />
          <MuiPickersUtilsProvider utils={DateFnsUtils} locale={locale}>
            <DateTimePicker
              autoOk
              allowKeyboardControl={false}
              disabled={isInputDisabled}
              format={dateTimeFormat}
              value={
                invalidDate ? this.dateStringToLocalDate(null) : parsedValue
              }
              onChange={value => {
                this.onDateChange(value, invalidDate)
              }}
              className="job-field-input-box"
              inputProps={{
                className: 'job-field-input',
                onFocus: can_set_current_datetime && this.onInputFocus,
              }}
              minDate={minDate}
              maxDate={maxDate}
              ampm={usaTimeFormat}
            />
            {value && !isInputDisabled && (
              <ClearIcon
                className="job-field-icon job-field-clear-icon"
                onClick={e => {
                  e.preventDefault()
                  this.onDateChange(null, invalidDate)
                }}
              />
            )}
          </MuiPickersUtilsProvider>
        </div>
        {showHint && (
          <span
            className={`job-field-alert-date-text${
              isInputDisabled ? '-disabled' : ''
            }`}>
            {message}
          </span>
        )}
        {invalidDate && (
          <span className="job-field-error-date-text">{message}</span>
        )}
      </FormControl>
    )
  }
}

export default connect(state => ({
  licensee: state.getIn(['profile', 'profile', 'licensee']),
  site: state.getIn(['profile', 'profile', 'site']),
}))(injectIntl(JobDateTimeInput))
